<template>
  <div>
    <v-row class="my-4" v-if="!loading && totalRecords > 0">
      <v-col cols="12" class="d-flex justify-end align-end">
        <ColumnsSelector
          persistenceKey="teamActivity__revsTab"
          :headers="headers"
          :limitActiveColumns="columnsLimit"
          @toggleColumn="onToggleColumn"
          @close="trackColumnsSelector"
          @setInitialColumns="setInitialColumns"
        />
      </v-col>
    </v-row>

    <v-data-table
      v-show="!loading && totalRecords > 0"
      data-private
      :headers="columns"
      :footer-props="footerOptions"
      :items="rows"
      :options.sync="tableOptions"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :server-items-length="totalRecords"
      :loading="loading"
      :mobile-breakpoint="$vuetify.breakpoint.thresholds.md"
      :no-data-text="$t('home.teams_activity.revs_tab_empty')"
      @click:row="trackRevNavigation"
    >
      <template v-slot:item.name="{ item }">
        <v-tooltip top max-width="300px">
          <template v-slot:activator="{ on }">
            <router-link class="router-link" :to="revRoute(item.type, item.id)">
              <span v-on="on" v-snip="1" class="body-2 link-td">{{
                item.name
              }}</span>
            </router-link>
          </template>
          {{ item.name }}
        </v-tooltip>
      </template>

      <template v-slot:item.typeText="{ item }">
        <EntityChip :entityType="item.type" />
      </template>

      <template v-slot:item.status="{ item }">
        <RevStatus :rev="item" showValidationIcons />
      </template>

      <template v-slot:item.owner="{ item }">
        <ChipAvatar small :account="item.owner" />
      </template>

      <template v-slot:item.categories="{ item }">
        <ChipList outlined :items="item.categories" />
      </template>

      <template v-slot:item.dueDate="{ item }">
        <span
          v-if="item.dueDate"
          class="body-2 blue-grey--text text--darken-3"
          >{{ item.dueDate }}</span
        >

        <div v-else class="text-start">-</div>
      </template>

      <template v-slot:item.tags="{ item }">
        <ChipList outlined :items="item.tags" />
      </template>

      <template v-slot:item.situationTypes="{ item }">
        <ChipList outlined :items="item.situationTypes" />
      </template>

      <template v-slot:item.coach="{ item }">
        <ChipAvatar small :account="item.coach" />
      </template>

      <template v-slot:item.collaborators="{ item }">
        <div v-if="item.collaborators.length === 0" class="text-start">-</div>

        <ChipAvatar small :account="item.collaborators[0]" />

        <span
          v-if="item.collaborators.length > 1"
          class="caption blue-grey--text text--darken-3"
        >
          +{{ item.collaborators.length - 1 }}
        </span>
      </template>

      <template v-slot:item.priority="{ item }">
        <PriorityChip :priority="item.priority" />
      </template>

      <template v-slot:item.challenge="{ item }">
        <span
          v-if="item.challenge"
          class="body-2 blue-grey--text text--darken-3"
          >{{ item.challenge }}</span
        >

        <div v-else class="text-start">-</div>
      </template>

      <template v-slot:item.isValidated="{ item }">
        <span class="body-2 blue-grey--text text--darken-3">
          {{ item.isValidated }}
        </span>
      </template>

      <template v-slot:item.isFinancialyValidated="{ item }">
        <span class="body-2 blue-grey--text text--darken-3">
          {{ item.isFinancialyValidated }}
        </span>
      </template>

      <template v-slot:item.createdAt="{ item }">
        <span class="body-2 blue-grey--text text--darken-3">
          {{ item.createdAt }}
        </span>
      </template>

      <template v-slot:item.completedAt="{ item }">
        <span class="body-2 blue-grey--text text--darken-3">{{
          item.completedAt
        }}</span>
      </template>

      <template v-slot:item.updatedAt="{ item }">
        <span class="body-2 blue-grey--text text--darken-3">
          {{ item.updatedAt }}
        </span>
      </template>

      <template v-slot:item.createdBy="{ item }">
        <ChipAvatar small :account="item.author" />
      </template>
    </v-data-table>

    <TeamActivityEmptyState v-if="!loading && totalRecords === 0" />

    <TableSkeleton v-if="loading" :columns="columns.length" class="pt-12" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { utils } from 'frontend-js-shared'
import mixin from '@/mixins/mixin.vue'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'
import api from '@/api'

import RevStatus from '@/components/Rev/RevStatus.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'
import PriorityChip from '@/components/PriorityChip/PriorityChip.vue'
import EntityChip from '@/components/EntityChip.vue'

import ColumnsSelector from '@/components/ColumnsSelector.vue'
import TableSkeleton from '@/components/TableSkeleton.vue'
import ChipList from '../ChipList.vue'
import TeamActivityEmptyState from './TeamActivityEmptyState.vue'

const INITIAL_ITEMS_PER_PAGE = 20

export default {
  name: 'RevsTab',

  props: {
    teamId: {
      type: String,
      required: false,
    },

    columnsLimit: {
      type: Number,
      required: false,
      default: null,
    },
  },

  mixins: [mixin, trackingMixin],

  components: {
    RevStatus,
    ChipAvatar,
    PriorityChip,
    ChipList,
    TeamActivityEmptyState,
    TableSkeleton,
    ColumnsSelector,
    EntityChip,
  },

  data() {
    return {
      revs: [],
      loading: false,
      totalRecords: 0,
      sortBy: 'updatedAt',
      sortDesc: true,
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      headers: [
        {
          text: this.$t('common.name'),
          value: 'name',
          show: true,
          width: 234,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('common.types'),
          value: 'typeText',
          show: true,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('common.status'),
          value: 'status',
          show: true,
          cellClass: 'cursor-pointer',
          width: 150,
        },
        {
          text: this.$t('common.roles.owner'),
          value: 'owner',
          show: true,
          cellClass: 'cursor-pointer',
        },
        {
          text: this.$t('common.category_other'),
          value: 'categories',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('common.due_date'),
          value: 'dueDate',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('common.tag_other'),
          value: 'tags',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('organization_settings.situation_type'),
          value: 'situationTypes',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('common.roles.coach_one'),
          value: 'coach',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('common.collaborator_other'),
          value: 'collaborators',
          show: false,
          cellClass: 'cursor-pointer',
        },
        {
          text: this.$t('common.priority'),
          value: 'priority',
          show: false,
          cellClass: 'cursor-pointer',
        },
        {
          text: this.$t('common.challenge_other'),
          value: 'challenge',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('common.rev_condition.validated'),
          value: 'isValidated',
          show: false,
          align: 'center',
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('rev.validation.financially_validated'),
          value: 'isFinancialyValidated',
          show: false,
          align: 'center',
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('filters.date.creation_date'),
          value: 'createdAt',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
          width: 150,
        },
        {
          text: this.$t('filters.date.modified_date'),
          value: 'updatedAt',
          show: true,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
          width: 150,
        },
        {
          text: this.$t('home.teams_activity.completion_date'),
          value: 'completedAt',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
          width: 150,
        },
        {
          text: this.$t('home.teams_activity.created_by'),
          value: 'createdBy',
          show: false,
          cellClass: 'cursor-pointer',
        },
      ],
    }
  },

  methods: {
    async fetchRevs(options) {
      try {
        if (!this.teamId) return
        this.loading = true
        const { data } = await api.stream.getOrganizationStream(
          this.organizationId,
          {
            filters: {
              organization: this.organizationId,
              teams: [this.teamId],
            },
            sort: {
              by: options.sortBy ?? 'updatedAt',
              order: options.sortDesc ? 'desc' : 'asc',
            },
            limit: options.itemsPerPage,
            from: options.itemsPerPage * (options.page - 1),
          }
        )
        this.revs = data.results ?? []
        this.totalRecords = data.totalRecordsInData ?? 0
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    revRoute(revType, revId) {
      if (revType === this.$constants.revTypes.problemSolving) {
        return `/problem-solving/${this.organizationId}/${revId}`
      }
      if (revType === this.$constants.revTypes.quickAction) {
        return `/quick-action/${this.organizationId}/${revId}`
      }
      return `/rev/${this.organizationId}/${revId}`
    },

    trackRevNavigation() {
      this.trackHomeNavigate({
        origin: 'teamActivity',
        destination: 'rev',
      })
    },

    getParsedCategories(categories) {
      return (
        categories?.map(category => this.$t(`common.categories.${category}`)) ??
        []
      )
    },

    getParsedTags(tags) {
      return tags?.map(tag => tag.name) ?? []
    },

    getParsedSituationTypes(situationTypes) {
      return (
        situationTypes?.map(situation =>
          this.$t(
            `quick_action.situation_types.situations.${situation.situation}`
          )
        ) ?? []
      )
    },

    onToggleColumn({ index, value }) {
      this.headers[index].show = value
    },

    trackColumnsSelector() {
      this.trackTeamNavigate({
        section: 'revsTab',
        element: 'columnsSelector',
        selectedColumns: this.headers
          .filter(col => col.show === true)
          .map(col => col.text),
      })
    },

    setInitialColumns(selectedColumns) {
      this.headers = this.getInitialSelectedTableColumns(
        this.headers,
        selectedColumns
      )
    },
  },

  computed: {
    ...mapGetters(['account', 'organizationId']),

    rows() {
      return this.revs.map(rev => {
        return {
          ...rev,
          id: rev.id,
          typeText: rev.type,
          name: rev.name,
          status: rev.status,
          owner: rev.owner,
          categories: this.getParsedCategories(rev.categories),
          dueDate: rev.dueDate
            ? utils.dates.formatDate(rev.dueDate, 'MMM D, YYYY, h:mm A')
            : '',
          tags: this.getParsedTags(rev.tags),
          situationTypes: this.getParsedSituationTypes(
            rev?.metadata?.beforePhase?.situationTypes
          ),
          coach: rev.coach,
          collaborators: rev.collaborators ?? [],
          priority: rev?.metadata?.priority,
          challenge: rev.challenge,
          isValidated: rev?.hasImplementationValidation
            ? this.$t('common.yes')
            : this.$t('common.no'),
          isFinancialyValidated: rev?.hasFinancialValidation
            ? this.$t('common.yes')
            : this.$t('common.no'),
          createDate: rev?.createdAt
            ? utils.dates.formatDate(rev.createdAt, 'MMM D, YYYY, h:mm A')
            : '-',
          completeDate: rev?.completedAt
            ? utils.dates.formatDate(rev.completedAt, 'MMM D, YYYY, h:mm A')
            : '-',
          updatedAt: rev?.updatedAt
            ? utils.dates.formatDate(rev.updatedAt, 'MMM D, YYYY, h:mm A')
            : '-',
        }
      })
    },

    columns() {
      return this.headers.filter(h => h.show)
    },

    tableOptions: {
      get() {
        return {
          itemsLength: this.a,
          itemsPerPage: INITIAL_ITEMS_PER_PAGE,
          page: 1,
          multiSort: false,
        }
      },

      set(event) {
        this.fetchRevs({
          itemsLength: this.totalRecords,
          itemsPerPage: event.itemsPerPage,
          page: event.page,
          sortDesc: event.sortDesc[0] ?? true,
          sortBy: event.sortBy[0],
          filters: {
            organization: this.organizationId,
            teams: [this.teamId],
          },
        })
      },
    },
  },

  watch: {
    teamId() {
      this.fetchRevs(this.tableOptions)
    },
  },
}
</script>
<style lang="scss" scoped>
.v-application a.router-link {
  color: $color-blue-grey-darken3;
  text-decoration: none;
}
</style>
